import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { FiFlag, FiImage } from 'react-icons/fi';
import { initials } from './metricTreeApi';

// MT-03: owner avatar shared with the Metric card, so a Strategy that has an
// Owner shows the same avatar (the panel offers Owner for both types).
function OwnerAvatar({ owner }) {
  if (!owner) return null;
  return (
    <span title={owner} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 20, height: 20, borderRadius: '50%', background: '#EEF1FB',
      color: '#4f5ed1', fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
    }}>{initials(owner)}</span>
  );
}

/**
 * Non-metric canvas nodes (Mixpanel palette parity): Strategy (a goal the metrics
 * roll up to), Note (sticky), Text (free label), Media (image). None are computed
 * — they carry structure + narrative. Content lives in `data.annotation`.
 */

const FONT = '"Open Sans", system-ui, sans-serif';
const handleDot = { width: 10, height: 10, background: '#94A3B8', border: '2px solid #fff' };

// Strategy / Bet — a goal or initiative. Header flag + title, then a target (if set)
// or a free-text body ("Write about your data…"). Connects up to the metric it
// supports (top source handle) as well as left/right.
function StrategyNodeBase({ data, selected }) {
  const a = data?.annotation || {};
  // MT-07: the description binds to annotation.text (editable in the panel).
  // Show it whenever present; a placeholder only when both description AND
  // target are empty (so the card never advertises an unfillable field).
  const description = a.text || a.description || '';
  const hasContent = description || a.target;
  return (
    <div style={{
      position: 'relative', width: 232, background: '#fff',
      border: `1px solid ${selected ? '#4f5ed1' : '#E3E9F1'}`,
      borderRadius: 14,
      boxShadow: selected ? '0 0 0 2px rgba(79,94,209,0.22), 0 10px 28px rgba(15,26,43,0.12)'
        : '0 1px 2px rgba(15,26,43,0.06), 0 6px 18px rgba(15,26,43,0.06)',
      padding: '13px 15px', fontFamily: FONT,
    }}>
      <Handle type="target" position={Position.Left} style={handleDot} />
      <Handle type="source" position={Position.Right} style={handleDot} />
      <Handle type="source" position={Position.Top} id="t" style={handleDot} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiFlag size={14} color="#64748B" />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#0F1A2B', letterSpacing: '-0.01em' }}>
          {data?.label || 'Bet'}
        </span>
        <OwnerAvatar owner={data?.owner} />
      </div>
      {description ? (
        <div style={{ marginTop: 9, fontSize: 12.5, color: '#475569', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{description}</div>
      ) : null}
      {a.target ? (
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748B', fontWeight: 600 }}>
          Target · <span style={{ color: '#0F1A2B', fontVariantNumeric: 'tabular-nums' }}>{a.target}</span>
        </div>
      ) : null}
      {!hasContent ? (
        <div style={{ marginTop: 9, fontSize: 12.5, color: '#94A3B8' }}>Add a description or target in the panel…</div>
      ) : null}
    </div>
  );
}

// Note — a sticky note (annotation only, no handles).
function NoteNodeBase({ data, selected }) {
  const a = data?.annotation || {};
  return (
    <div style={{
      width: 200, minHeight: 72, background: a.color || '#FEF3C7',
      border: `1px solid ${selected ? '#D9A400' : '#F2DE9B'}`, borderRadius: 10,
      boxShadow: '0 1px 2px rgba(120,80,0,0.10), 0 6px 16px rgba(120,80,0,0.08)',
      padding: 12, fontFamily: FONT, fontSize: 13, lineHeight: 1.45, color: '#713F12',
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
      {a.text || 'Double-click to edit note'}
    </div>
  );
}

// Text — a free-standing label (no card chrome, no handles). Optional gray
// description line (used for the KPI level bands: title + explanation).
function TextNodeBase({ data, selected }) {
  const a = data?.annotation || {};
  return (
    <div style={{
      fontFamily: FONT, padding: '4px 6px', borderRadius: 6, maxWidth: 240,
      background: selected ? 'rgba(79,94,209,0.06)' : 'transparent',
      outline: selected ? '1px dashed rgba(79,94,209,0.4)' : 'none',
    }}>
      <div style={{ fontSize: a.size || 15, fontWeight: 800, color: '#0F1A2B', letterSpacing: '-0.01em' }}>
        {data?.label || a.content || 'Text'}
      </div>
      {a.description ? (
        <div style={{ marginTop: 4, fontSize: 12, color: '#94A3B8', lineHeight: 1.4 }}>{a.description}</div>
      ) : null}
    </div>
  );
}

// Media — an image with an optional caption. Falls back to a labelled placeholder
// when the image is missing OR fails to load (BUG-MT-08) instead of collapsing to
// a broken-image sliver.
function MediaNodeBase({ data, selected }) {
  const a = data?.annotation || {};
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [a.url]); // retry when the URL changes
  const showImg = a.url && !broken;
  return (
    <div style={{
      width: 230, background: '#fff',
      border: `1px solid ${selected ? '#4f5ed1' : '#E3E9F1'}`, borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(15,26,43,0.06), 0 6px 18px rgba(15,26,43,0.06)', fontFamily: FONT,
    }}>
      {showImg ? (
        // eslint-disable-next-line jsx-a11y/img-redundant-alt
        <img
          src={a.url}
          alt={a.caption || 'media'}
          onError={() => setBroken(true)}
          style={{ width: '100%', display: 'block', maxHeight: 170, objectFit: 'cover' }}
        />
      ) : (
        <div style={{
          height: 120, display: 'grid', placeItems: 'center', color: '#94A3B8',
          background: '#F6F8FB', gap: 6, textAlign: 'center', padding: '0 12px',
        }}>
          <FiImage size={26} />
          <span style={{ fontSize: 11 }}>
            {a.url ? 'Image failed to load' : 'Upload or paste an image in the panel'}
          </span>
        </div>
      )}
      {a.caption ? (
        <div style={{ padding: '8px 10px', fontSize: 12, color: '#64748B', fontWeight: 500 }}>{a.caption}</div>
      ) : null}
    </div>
  );
}

export const StrategyNode = memo(StrategyNodeBase);
export const NoteNode = memo(NoteNodeBase);
export const TextNode = memo(TextNodeBase);
export const MediaNode = memo(MediaNodeBase);
